<script setup lang="ts">
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { breakpointsTailwind, useScrollLock } from '@vueuse/core';
import { gsap } from 'gsap';

/**
 * Base Dialog
 * --------------------------------
 * Dialog component (based on <dialog> element) with predefined style and animation.
 * Locks focus and scrolling.
 *
 * - dark / light backdrop
 * - as drawer: slide in from bottom on mobile (optional)
 */

const {
    backdrop = 'dark',
    animateDrawer = false,

} = defineProps<{
    backdrop?: 'dark' | 'light' | 'light-bg';
    animateDrawer?: boolean;
}>();

// closed event means the dialog is closed visually and should be unmounted
const emit = defineEmits(['closed']);

const elDialog = useTemplateRef('elDialog');
const elContent = useTemplateRef('elContent');
const elBackdrop = useTemplateRef('elBackdrop');
const windowIsLocked = useScrollLock(window);
const breakpoints = useBreakpoints(breakpointsTailwind);
const preferredReducedMotion = usePreferredReducedMotion();
onMounted(() => {
    windowIsLocked.value = true;
});
onUnmounted(() => {
    windowIsLocked.value = false;
});

const backdropClasses = computed(() => ({
    'bg-brand-b/90': backdrop === 'dark',
    'bg-shade-3/90': backdrop === 'light',
    'bg-background-1/90': backdrop === 'light-bg',
}));

/**
 * Control Open/Close State
 * --------------------------------------------------------------------------------------------------
 */

/**
 * Bienvenuti!
 *
 * <dialog> needs to explicitely be opened with .showModal()
 * Thats why we need to call it manually on open
 *
 * Usually, BaseDialog is directly opened when mounted
 * (and unmounted after closed)
 */
async function open() {
    animateOpen();

    // Safety catch for safari
    if (elDialog.value && ('isConnected' in elDialog.value ? elDialog.value.isConnected : true)) {
        elDialog.value.showModal();
    }
}

/**
 * Ciao!
 *
 * <dialog> needs to explicitely be closed with .close()
 * Thats why we need to call it manually on close
 *
 * - Animates the closing, then:
 *   - closes <dialog> in DOM
 *   - emits 'closed' event, which would then remove the whole dialog component
 */
function close(e?: Event) {
    e?.preventDefault();

    animateClose(() => {
        elDialog.value?.close();
        emit('closed');
    });
}

/**
 * Trigger closing on <dialog> cancel (esc key or clicking outside)
 */
onMounted(() => elDialog.value?.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
}));

/**
 * Animations
 * --------------------------------------------------------------------------------------------------
 */

const { isChrome } = useDeviceOs();

const yTransform = computed(() => {
    if (animateDrawer && breakpoints.smaller('md').value) {
        return '100%';
    }
    return 200;
});

/**
 * Animated opening
 */
function animateOpen() {
    if (preferredReducedMotion.value === 'reduce') {
        return;
    }

    const tl = gsap.timeline();
    const slidingElements = elContent.value?.querySelectorAll('[data-transition-slide]') ?? [];
    const fadingElements = elContent.value?.querySelectorAll('[data-transition-fade]') ?? [];

    if (elBackdrop.value) {
        tl.fromTo(elBackdrop.value, { opacity: 0 }, { opacity: 1, duration: 0.3 },
        );
    }
    if (slidingElements.length) {
        tl.set(slidingElements, { y: yTransform.value, opacity: 0 }, 0);
        tl.fromTo(slidingElements, { opacity: 1, y: yTransform.value }, { opacity: 1, y: 0, duration: 0.3, ease: 'power3.out', immediateRender: false }, 0);
    }
    if (fadingElements.length) {
        tl.fromTo(fadingElements, { opacity: 0 }, { opacity: 1, duration: 0.3 }, '<25%');
    }
};

/**
 * Animated closing
 */
function animateClose(closeCallback: () => void) {
    if (preferredReducedMotion.value === 'reduce') {
        closeCallback();
        return;
    }

    const tl = gsap.timeline();
    const slidingElements = elContent.value?.querySelectorAll('[data-transition-slide]') ?? [];
    const fadingElements = elContent.value?.querySelectorAll('[data-transition-fade]') ?? [];

    if (fadingElements.length) {
        tl.to(fadingElements, { opacity: 0, duration: 0.3 }, 0);
    }
    if (slidingElements.length) {
        tl.to(slidingElements, { opacity: 0, y: yTransform.value, duration: 0.3, ease: 'power1.in' }, 0);
    }
    if (elBackdrop.value) {
        tl.to(elBackdrop.value, { opacity: 0, duration: 0.3 }, 0.2);
    }
    tl.call(closeCallback);
};

/**
 * Animated going to next item (animate swipe left)
 */
function animateNextItem(nextItemCallback: () => void) {
    const t1 = gsap.timeline();
    const nextElements = elContent.value?.querySelectorAll('[data-transition-next]') ?? [];

    if (nextElements.length) {
        t1.fromTo(nextElements, { opacity: 1, x: 0 }, { opacity: 0, x: -200, duration: 0.3, ease: 'power3.in', onComplete: nextItemCallback });
        t1.fromTo(nextElements, { opacity: 0, x: 200 }, { opacity: 1, x: 0, duration: 0.3, ease: 'power2.out', immediateRender: false });
    }
    else {
        nextItemCallback();
    }
};

/**
 *
 */
defineExpose({
    open,
    close,
    animateNextItem,
});
</script>

<template>
    <dialog
        ref="elDialog"
        class="
            base-dialog
            pointer-events-auto
            m-0
            h-dvh
            max-h-none
            w-screen
            max-w-none
            bg-transparent
            p-0
            text-brand-b
        "
    >
        <div
            ref="elContent"
            class="
                grid
                h-full
                grid-rows-[min-content_auto]
            "
        >
            <div class="sticky top-0 z-21 print:hidden">
                <TheToasts />
            </div>

            <slot />
        </div>

        <div
            ref="elBackdrop"
            class="
                fixed
                inset-0
                -z-10
                backdrop-blur-sm
                duration-50
                data-[reduced-blur=true]:backdrop-blur-xs
            "
            :data-reduced-blur="isChrome"
            :class="backdropClasses"
        />
        <TheLoadingOverlay :mode="backdrop === 'dark' ? 'dark' : 'light'" />
        <ClientOnly>
            <TheDevHelper v-if="useRuntimeConfig().public.enableDevHelpers" />
            <VueQueryDevtools
                position="right"
                button-position="bottom-left"
                class="print:hidden"
            />
        </ClientOnly>
    </dialog>
</template>

<style scoped>
    .base-dialog {
        scrollbar-width: thin;
        scrollbar-gutter: stable;
    }
</style>
